import styled from 'styled-components'
import { createContext, FC, useContext, useRef, useState } from 'react'

import { Modal } from 'Components/Modal/Modal'
import { StyledButton } from 'Components/Buttons/StyledButton'
import { ModalHeader } from 'Components/Modal/ModalHeader'

import {
  ConfirmationModalContent,
  ConfirmationModalContextProviderProps,
  useModalShow
} from 'Hooks/modals/useModalShow'

import { spacingSM, spacingXS } from 'Utils/styles/spacing'
import { ThinDarkGreenText } from 'Utils/styles/text'
import { FormDatePicker } from 'Components/Inputs/Calendar/FormDatePicker'
import { createDate } from 'Utils/helpers/dateUtils'
import { CountryCode, CountryNames } from 'Utils/types/countries'
import { Employer } from 'Utils/types/JOONAdmin/employer'
import { StyledSelect } from 'Components/Inputs/StyledSelect'

const ButtonContainer = styled.div`
  display: flex;
  gap: ${spacingSM};
  justify-content: end;
`

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacingXS};
`

interface GeneratePurchaseReportModalResult {
  date: Date
  countryCode: CountryCode
}

interface GeneratePurchaseReportModalOptions {
  employer: Employer
}

interface ModalContextType {
  showModal: (
    options: GeneratePurchaseReportModalOptions
  ) => Promise<GeneratePurchaseReportModalResult | undefined>
}

const UseGeneratePurchaseReportModal = createContext<ModalContextType>({} as ModalContextType)

const GeneratePurchaseReportModalProvider: FC<ConfirmationModalContextProviderProps> = (props) => {
  const { setShow, show, onHide } = useModalShow()

  const [selectedDate, setSelectedDate] = useState(createDate())
  const [selectedCountry, setSelectedCountry] = useState<CountryCode>(CountryCode.US)
  const [availableCountries, setAvailableCountries] = useState<CountryCode[]>([])
  const [content, setContent] = useState<ConfirmationModalContent | null>()
  const resolver =
    useRef<
      (
        value:
          | GeneratePurchaseReportModalResult
          | undefined
          | PromiseLike<GeneratePurchaseReportModalResult | undefined>
      ) => void
    >()

  const showModal = (
    options: GeneratePurchaseReportModalOptions
  ): Promise<GeneratePurchaseReportModalResult | undefined> => {
    const { employer } = options

    setContent({
      title: 'Regenerate Purchase Report - Select Month',
      message:
        'Select the month and country for which to regenerate the purchase report. The report will be regenerated in Google Drive, and a link will be sent to #reporting on Slack.',
      confirmButtonId: 'continue.label',
      cancelButtonId: 'cancel.label'
    })

    setAvailableCountries(employer.countries)
    setSelectedCountry(employer.countries[0] || CountryCode.US)

    setShow(true)
    return new Promise((resolve) => {
      resolver.current = resolve
    })
  }

  const handleOk = () => {
    if (resolver.current) {
      resolver.current({
        date: selectedDate,
        countryCode: selectedCountry
      })
    }
    onHide()
  }

  const handleCancel = () => {
    if (resolver.current) {
      resolver.current(undefined)
    }
    onHide()
  }

  return (
    <UseGeneratePurchaseReportModal.Provider
      value={{
        showModal
      }}
    >
      {props.children}

      {content && (
        <Modal showModal={show} setShowModal={onHide}>
          <ModalHeader>{content.title}</ModalHeader>

          {content.message && <ThinDarkGreenText>{content.message}</ThinDarkGreenText>}

          <ModalContent>
            <FormDatePicker date={selectedDate} onChange={setSelectedDate} disabled={false} />

            {availableCountries.length > 1 && (
              <StyledSelect
                options={availableCountries.map((countryCode) => ({
                  label: CountryNames[countryCode],
                  value: countryCode
                }))}
                handleChange={(value: string) => setSelectedCountry(value as CountryCode)}
                placeholderText="Select a country"
                defaultValue={selectedCountry}
              />
            )}
          </ModalContent>

          <ButtonContainer>
            <StyledButton id={content.cancelButtonId} onClick={handleCancel} />
            <StyledButton id={content.confirmButtonId} primary onClick={handleOk} />
          </ButtonContainer>
        </Modal>
      )}
    </UseGeneratePurchaseReportModal.Provider>
  )
}

const useGeneratePurchaseReportModal = (): ModalContextType =>
  useContext(UseGeneratePurchaseReportModal)

export { useGeneratePurchaseReportModal }

export default GeneratePurchaseReportModalProvider
